import { BElement } from "utils/base-component"
import { LARGE_UP } from "./desktop-dropdowns"

export class MobileMenu {
  private cssClass: string
  private navHeader?: BElement
  private logoContainer?: BElement
  private ctaContainer?: BElement
  private menuBtn?: BElement
  private mobileMenu?: BElement
  private mobSearchBtn?: BElement
  private searchContainer?: BElement
  private searchForm?: BElement
  private searchInput?: BElement
  private windowWidth = 0
  private abortController = new AbortController()

  constructor(cssClass: string) {
    this.cssClass = cssClass
    this.initializeElements()
    this.addEventListeners()
  }

  private initializeElements() {
    const getElement = (selector: string) => {
      const element = document.querySelector(selector)
      return element ? new BElement(element, this.cssClass) : undefined
    }

    this.navHeader = getElement(".page-header__nav-header")
    this.logoContainer = getElement(".page-header__logo-container")
    this.ctaContainer = getElement(".page-header__header-cta")
    this.menuBtn = getElement(".page-header__button--mobile-menu")
    this.mobileMenu = getElement(".mobile-menu")
    this.mobSearchBtn = getElement(".location-nav-header-text-field__mob-search-button")
    this.searchContainer = getElement(".location-nav-header-text-field")
    this.searchForm = getElement(".location-nav-header-text-field__search-form")
    this.searchInput = getElement(".location-nav-header-text-field__search-input")
  }

  private addEventListeners() {
    const { signal } = this.abortController

    this.menuBtn?.element.addEventListener(
      "click",
      (e) => {
        e.preventDefault()
        this.toggleMenu()
      },
      { signal },
    )

    this.mobSearchBtn?.element.addEventListener(
      "click",
      (e) => {
        e.preventDefault()
        this.openSearch()
      },
      { signal },
    )

    document.addEventListener(
      "click",
      (e) => {
        const target = e.target as Node
        if (
          this.searchContainer?.element &&
          this.searchContainer.element.classList.contains("search-active") &&
          !this.searchContainer.element.contains(target) &&
          !this.mobSearchBtn?.element.contains(target)
        ) {
          this.closeSearch()
        }
      },
      { signal },
    )

    // special case for reveal search button as it only shows on smallest screen sizes
    this.windowWidth = window.innerWidth
    window.addEventListener(
      "resize",
      () => {
        if (this.windowWidth === window.innerWidth) return

        this.windowWidth = window.innerWidth
        if (this.mobileMenu?.element.classList.contains("active")) this.positionMenu()
        if (!this.isCompactSearchMode()) {
          this.closeSearch()
        }
      },
      { signal },
    )

    window.matchMedia(LARGE_UP).addEventListener(
      "change",
      (event) => {
        if (event.matches) this.closeAllMobileMenus()
      },
      { signal },
    )

    this.closeSearch()
  }

  private isCompactSearchMode(): boolean {
    if (!this.mobSearchBtn?.element) return false
    return window.getComputedStyle(this.mobSearchBtn.element).display !== "none"
  }

  private openSearch(): void {
    this.searchContainer?.element.classList.add("search-active")
    this.logoContainer?.element.classList.add("search-active")
    this.ctaContainer?.element.classList.add("search-active")
    this.mobSearchBtn?.element.classList.add("active")
    this.mobSearchBtn?.element.setAttribute("aria-expanded", "true")
    this.searchForm?.element.setAttribute("aria-hidden", "false")
    this.searchInput?.element.focus()
  }

  private closeSearch(): void {
    this.searchContainer?.element.classList.remove("search-active")
    this.logoContainer?.element.classList.remove("search-active")
    this.ctaContainer?.element.classList.remove("search-active")
    this.mobSearchBtn?.element.classList.remove("active")
    this.mobSearchBtn?.element.setAttribute("aria-expanded", "false")
    this.searchForm?.element.setAttribute(
      "aria-hidden",
      this.isCompactSearchMode() ? "true" : "false",
    )
  }

  // Methods to control sticky footer ad visibility
  private static hideStickyAd(): void {
    const stickyAd = document.querySelector("#adb-adhesion-container-1")
    if (stickyAd instanceof HTMLElement) {
      stickyAd.style.display = "none"
    }
  }

  private static showStickyAd(): void {
    const stickyAd = document.querySelector("#adb-adhesion-container-1")
    if (stickyAd instanceof HTMLElement) {
      stickyAd.style.display = ""
    }
  }

  private toggleMenu(): void {
    const menuElement = this.mobileMenu?.element
    if (!menuElement) return

    const currentDisplay = menuElement.style.display
    const isVisible = currentDisplay !== "none" && currentDisplay !== ""

    this.closeAllMobileMenus()
    if (isVisible) return

    menuElement.style.display = "block"
    menuElement.classList.add("active")
    document.body.style.overflow = "hidden"
    document.body.style.position = "fixed"
    MobileMenu.hideStickyAd()
    this.navHeader?.element.classList.add("mm-active")
    this.positionMenu()

    const button = this.menuBtn?.element
    if (button) {
      if (button.hasAttribute("aria-expanded")) {
        button.setAttribute("aria-expanded", "true")
      }
      button.classList.add("active")
    }
  }

  private positionMenu(): void {
    const navHeaderBottom = this.navHeader?.element.getBoundingClientRect().bottom ?? 0
    if (this.mobileMenu) this.mobileMenu.element.style.top = `${navHeaderBottom}px`
  }

  closeAllMobileMenus(): void {
    const menuElement = this.mobileMenu?.element
    if (menuElement) {
      menuElement.style.display = "none"
      menuElement.classList.remove("active")
    }

    const button = this.menuBtn?.element
    if (button) {
      if (button.hasAttribute("aria-expanded")) {
        button.setAttribute("aria-expanded", "false")
      }
      button.classList.remove("active")
    }

    document.body.style.overflow = ""
    document.body.style.position = "relative"
    MobileMenu.showStickyAd()
    this.navHeader?.element.classList.remove("mm-active")
  }

  // check if click is inside menus (for document click handler)
  isMenuClick(target: Node): boolean {
    return !!(
      (this.mobileMenu?.element && this.mobileMenu.element.contains(target)) ||
      (this.searchForm?.element && this.searchForm.element.contains(target)) ||
      (this.searchInput?.element && this.searchInput.element.contains(target))
    )
  }

  // check if click is on menu buttons (for document click handler)
  isMenuButtonClick(target: Node): boolean {
    return !!(
      (this.menuBtn?.element && this.menuBtn.element.contains(target)) ||
      (this.mobSearchBtn?.element && this.mobSearchBtn.element.contains(target))
    )
  }

  destroy(): void {
    this.abortController.abort()
  }
}
